import { Injectable } from '@angular/core';
import { PreloadingStrategy, Route } from '@angular/router';
import { Observable, of, switchMap, timer } from 'rxjs';

/** After a page has loaded, how long the others wait before their code is fetched. */
const PRELOAD_AFTER_MS = 2500;

/**
 * Fetches the code of the pages the header links to (routes with `data: { preload: true }`)
 * once the page in front of the visitor has settled and the browser is idle, so moving from
 * page to page never waits on the network. Visitors who asked to save data, or on a 2G
 * connection, fetch each page only when they open it.
 */
@Injectable({ providedIn: 'root' })
export class IdlePreloading implements PreloadingStrategy {
  preload(route: Route, load: () => Observable<unknown>): Observable<unknown> {
    if (!route.data?.['preload'] || !worthPreloading()) return of(null);
    return timer(PRELOAD_AFTER_MS).pipe(
      switchMap(() => whenIdle()),
      switchMap(() => load()),
    );
  }
}

function worthPreloading(): boolean {
  if (typeof navigator === 'undefined') return false;
  const c = (navigator as Navigator & { connection?: { saveData?: boolean; effectiveType?: string } }).connection;
  return !(c?.saveData || /(^|-)2g$/.test(c?.effectiveType ?? ''));
}

function whenIdle(): Observable<void> {
  return new Observable<void>((sub) => {
    const done = () => {
      sub.next();
      sub.complete();
    };
    if (typeof requestIdleCallback === 'function') {
      const id = requestIdleCallback(done, { timeout: 4000 });
      return () => cancelIdleCallback(id);
    }
    const id = setTimeout(done, 200);
    return () => clearTimeout(id);
  });
}
